import type { ReactNode } from "react";

import {
  HoverHintDefinitionGrid,
  skuQuantityPeekRows,
  type HoverHintDefinitionRow,
} from "@/components/ui/HoverHintDefinitionGrid";

import { SalesOrderListPeekHoverHintBody } from "./entityPeekBodies";

export type SalesOrderPeekContext = {
  sales_order_number?: string | null;
  client_name?: string | null;
  end_user_name?: string | null;
  customer_purchase_order_number?: string | null;
  sgs_ship_by_date?: string | null;
  lines?: ReadonlyArray<{ sku: string; quantity: number }>;
};

function formatShipByDate(isoDay: string | null | undefined): string {
  if (isoDay == null || isoDay.trim() === "") return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDay.trim());
  if (match == null) return isoDay.trim();
  const [, yyyy, mm, dd] = match;
  return `${mm}-${dd}-${yyyy}`;
}

function buildSalesOrderPeekRows(context: SalesOrderPeekContext): HoverHintDefinitionRow[] {
  const rows: HoverHintDefinitionRow[] = [];
  if (context.client_name != null && context.client_name.trim() !== "") {
    rows.push({ label: "Client", value: context.client_name.trim() });
  }
  if (context.end_user_name != null && context.end_user_name.trim() !== "") {
    rows.push({ label: "End user", value: context.end_user_name.trim() });
  }
  if (context.customer_purchase_order_number != null && context.customer_purchase_order_number.trim() !== "") {
    rows.push({ label: "PO", value: context.customer_purchase_order_number.trim() });
  }
  if (context.sgs_ship_by_date != null && context.sgs_ship_by_date.trim() !== "") {
    rows.push({ label: "Ship by", value: formatShipByDate(context.sgs_ship_by_date) });
  }
  rows.push(...skuQuantityPeekRows(context.lines ?? []));
  if (rows.length === 0 && context.sales_order_number != null && context.sales_order_number.trim() !== "") {
    rows.push({ label: "Sales order", value: context.sales_order_number.trim() });
  }
  return rows;
}

export function SalesOrderPeekHoverHintBody({
  context,
  emptyMessage = "No sales order context.",
}: {
  context: SalesOrderPeekContext | undefined;
  emptyMessage?: ReactNode;
}): ReactNode {
  if (context == null) {
    return <span className="text-slate-500">{emptyMessage}</span>;
  }
  const rows = buildSalesOrderPeekRows(context);
  if (rows.length === 0) {
    return <span className="text-slate-500">{emptyMessage}</span>;
  }
  return <HoverHintDefinitionGrid rows={rows} />;
}

export function salesOrderPeekContextsFromCalendar(
  contexts: ReadonlyArray<{
    salesOrderNumber?: string | null;
    clientName?: string | null;
    endUserName?: string | null;
    sgsShipByDate?: string | null;
  }>,
): ReactNode {
  return <SalesOrderListPeekHoverHintBody contexts={contexts} />;
}
